import React from 'react';
import { Card, List, Avatar, Typography, Tag, Button } from 'antd';
import { 
    TrophyOutlined, 
    CalendarOutlined,
    RightOutlined,
    CrownOutlined,
    UserOutlined,
    BranchesOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { TopPayingCompany } from '../../../../types/super-admin.types';

const { Title, Text } = Typography;

interface TopPayingCompaniesProps {
    companies: TopPayingCompany[];
    loading?: boolean;
}

const TopPayingCompanies: React.FC<TopPayingCompaniesProps> = ({ companies, loading = false }) => {
    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: 'TRY',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    const getRankIcon = (index: number) => {
        switch (index) {
            case 0:
                return <CrownOutlined style={{ color: '#FFD700', fontSize: '18px' }} />;
            case 1:
                return <TrophyOutlined style={{ color: '#C0C0C0', fontSize: '16px' }} />;
            case 2:
                return <TrophyOutlined style={{ color: '#CD7F32', fontSize: '16px' }} />;
            default:
                return <span className="fw-bold text-muted">#{index + 1}</span>;
        }
    };

    const getRankColor = (index: number) => {
        switch (index) {
            case 0: return '#FFD700';
            case 1: return '#C0C0C0';
            case 2: return '#CD7F32';
            default: return '#f0f0f0';
        }
    };

    const getStatusColor = (status: string) => {
        switch (status?.toLowerCase()) {
            case 'active': return 'success';
            case 'inactive': return 'default';
            case 'expired': return 'error';
            case 'trial': return 'processing';
            case 'in_grace_period': return 'warning';
            default: return 'default';
        }
    };

    const translateStatus = (status: string) => {
        switch (status?.toLowerCase()) {
            case 'active': return 'Aktif';
            case 'inactive': return 'Pasif';
            case 'expired': return 'Süresi Dolmuş';
            case 'trial': return 'Deneme';
            case 'in_grace_period': return 'Tolerans Süresinde';
            default: return status;
        }
    };

    return (
        <Card 
            className="border-0 shadow-sm"
            loading={loading}
            title={
                <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                        <div className="me-2 p-2 bg-warning bg-opacity-10 rounded">
                            <TrophyOutlined className="text-warning" />
                        </div>
                        <div>
                            <Title level={5} className="mb-0">En Çok Ödeme Yapan Şirketler</Title>
                            <small className="text-muted">Harcama miktarına göre lider tablosu</small>
                        </div>
                    </div>
                    <Link to="/superadmin/payment-reports">
                        <Button type="text" size="small" icon={<RightOutlined />}>
                            Tümünü Gör
                        </Button>
                    </Link>
                </div>
            }
            bodyStyle={{ padding: '0' }}
        >
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <List
                    itemLayout="horizontal"
                    dataSource={companies}
                    renderItem={(company, index) => (
                        <List.Item
                            className="px-4 py-3 border-0"
                            style={{ 
                                backgroundColor: index < 3 ? `${getRankColor(index)}10` : 'transparent',
                                borderLeft: index < 3 ? `3px solid ${getRankColor(index)}` : 'none'
                            }}
                            actions={[
                                <div className="text-end">
                                    <div className="fw-bold text-success mb-1">
                                        {formatCurrency(company.totalSpent)}
                                    </div>
                                    <div className="small text-muted">
                                        {company.transactionCount} işlem
                                    </div>
                                </div>
                            ]}
                        >
                            <List.Item.Meta
                                avatar={
                                    <div className="position-relative">
                                        <Avatar 
                                            size={44}
                                            style={{ 
                                                backgroundColor: getRankColor(index),
                                                color: index < 3 ? '#fff' : '#666',
                                                fontWeight: 'bold'
                                            }}
                                        >
                                            {company.name.charAt(0).toUpperCase()}
                                        </Avatar>
                                        <div 
                                            className="position-absolute"
                                            style={{ 
                                                top: '-5px', 
                                                right: '-5px',
                                                backgroundColor: '#fff',
                                                borderRadius: '50%',
                                                padding: '2px',
                                                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                            }}
                                        >
                                            {getRankIcon(index)}
                                        </div>
                                    </div>
                                }
                                title={
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <Text strong className="d-block">
                                                {company.name}
                                            </Text>
                                            <div className="mt-1">
                                                <Tag color={getStatusColor(company.status)} className="me-2">
                                                    {translateStatus(company.status)}
                                                </Tag>
                                                <Text type="secondary" className="small me-2">
                                                    <UserOutlined className="me-1" />{company.userCount}
                                                </Text>
                                                <Text type="secondary" className="small">
                                                    <BranchesOutlined className="me-1" />{company.branchCount}
                                                </Text>
                                            </div>
                                        </div>
                                    </div>
                                }
                                description={
                                    <div className="small text-muted mt-1">
                                        <CalendarOutlined className="me-1" />
                                        Kayıt: {dayjs(company.createdAt).format('DD.MM.YYYY')}
                                    </div>
                                }
                            />
                        </List.Item>
                    )}
                />
            </div>
        </Card>
    );
};

export default TopPayingCompanies; 